import { toast } from 'react-toastify';

const API_URL = 'https://alpha.currentjobs.co.ke/api/jobs';

const getAuthHeaders = async (isJson = true) => {
  if (window.keycloak) {
    try {
      await window.keycloak.updateToken(30);
    } catch (error) {
      console.warn('Failed to refresh token, forcing login');
      window.keycloak.login();
    }
  }
  const headers = {
    'Authorization': `Bearer ${window.keycloak?.token}`,
  };
  if (isJson) {
    headers['Content-Type'] = 'application/json';
  }
  return headers;
};

const handleResponse = async (res, successMessage = null, errorMessage = 'An error occurred') => {
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) {
      toast.error('Unauthorized access. Please login again.');
      window.keycloak?.login();
      throw new Error('Unauthorized');
    }
    const errorText = await res.text().catch(() => '');
    toast.error(errorMessage);
    throw new Error(`API Error: ${res.status} ${errorText}`);
  }
  
  if (successMessage) {
    toast.success(successMessage);
  }
  
  // Return JSON if it has content, otherwise return null
  try {
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  } catch (e) {
    return null;
  }
};

export const fetchJobs = async (page, size) => {
  const headers = await getAuthHeaders(false);
  const res = await fetch(`${API_URL}/viewall?page=${page}&size=${size}`, {
    method: 'GET',
    headers,
  });
  return await handleResponse(res, null, 'Failed to load job listings');
};

export const deleteJob = async (id) => {
  const headers = await getAuthHeaders(false);
  const res = await fetch(`${API_URL}/deletejob/${id}`, {
    method: 'DELETE',
    headers,
  });
  await handleResponse(res, 'Job deleted successfully', 'Failed to delete job');
};

export const adddJob = async (jobData) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(jobData),
  });
  return await handleResponse(res, 'Job added successfully', 'Failed to add the job');
};

export const updateJob = async (id, jobData) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/updatejob/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(jobData),
  });
  return await handleResponse(res, 'Job updated successfully', 'Failed to update the job');
};

export const fetchJobById = async (id) => {
  const headers = await getAuthHeaders(false);
  const res = await fetch(`${API_URL}/cl/viewjob/${id}`, {
    method: 'GET',
    headers,
  });
  return await handleResponse(res, null, 'Failed to fetch job details');
};

export const fetchActiveJobCount = async () => {
  try {
    const headers = await getAuthHeaders(false);
    const res = await fetch(`${API_URL}/cl/count/active`, { headers });
    return await handleResponse(res, null, 'Failed to fetch active job count');
  } catch {
    return 0;
  }
};

export const fetchExpiredJobCount = async () => {
  try {
    const headers = await getAuthHeaders(false);
    const res = await fetch(`${API_URL}/expired`, { headers });
    return await handleResponse(res, null, 'Failed to count Expired Jobs');
  } catch {
    return 0;
  }
};

export const fetchAddedTodayJobCount = async () => {
  try {
    const headers = await getAuthHeaders(false);
    const res = await fetch(`${API_URL}/current`, {
      method: 'GET',
      headers,
    });
    return await handleResponse(res, null, 'Failed to fetch jobs added today');
  } catch {
    return 0;
  }
};

export const filterJobs = async (filterRequest) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/filterr`, {
    method: 'POST',
    headers,
    body: JSON.stringify(filterRequest)
  });
  return await handleResponse(res, null, 'Failed to apply filters');
};

export const checkJobExists = async ({ companyName, title, closingDate }) => {
  try {
    const headers = await getAuthHeaders(false);
    const params = new URLSearchParams({ companyName, title, closingDate });
    const res = await fetch(`${API_URL}/exists?${params.toString()}`, {
      method: 'GET',
      headers
    });
    const data = await handleResponse(res, null, 'Failed to check job existence');
    return data?.exists || false;
  } catch {
    return false;
  }
};

export const addJob = async (jobs) => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_URL}/bulk`, {
    method: 'POST',
    headers,
    body: JSON.stringify(jobs)
  });
  return await handleResponse(res, null, 'Failed to add jobs');
};